import styles from "../forms.module.css";
import { TSmsMessage } from "../../../services/types/utils.types";
import React, { ChangeEvent, useRef, useState } from "react";
import CloseButton from "../../closeButton/closeButton";
import { usePostSmsMessageMutation } from "../../../services/api/utils.api";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import Modal from "../../modals/modal/Modal";
import GeneralSuccessPopup from "../../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../modals/errorPopup/errorPopup";
import useAutosizeTextArea from "../../../hooks/useAutosizeTextarea";
import Select, { ActionMeta, SingleValue } from "react-select";
import { getStatusOptions } from "../../../utils/constants";
import { TLabeledItem } from "../../../types/types";
import { tWithKeyAttr as t } from "../../../i18nHelper";


interface IAddOrEditSmsFormProps {
  form: TSmsMessage;
  handleChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onClose: () => void;
  handleSelectChange: (
    newValue: SingleValue<TLabeledItem>,
    actionMeta: ActionMeta<TLabeledItem>
  ) => void;
}

function AddOrEditSmsForm({
  form,
  handleChange,
  onClose,
  handleSelectChange,
}: IAddOrEditSmsFormProps) {
  const admin = useAppSelector(adminSelector);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [postSmsMessage, { isLoading }] = usePostSmsMessageMutation();
  const statusOptions = getStatusOptions(); 

  useAutosizeTextArea(textAreaRef.current, form.body || "");

  const onSubmit = () => {
    postSmsMessage({
      companyId: admin.companyId,
      body: { ...form, companyId: admin.companyId },
    })
      .then(() => {
        setOpenModalSuccess(true);
      })
      .catch((err) => {
        // console.log(`error sending sms: ${err}`);
        setOpenModalError(true);
      });
  };

  return (
    <form className={styles.form}>
      <CloseButton onClick={onClose} />
      <h3 className={styles.form__title}>
        {form.id ? t('EDIT_SMS_1') : t('CREATE_NEW_SMS')}
      </h3>
      <ul
        className={`${styles.form__list} ${styles.form__list_type_oneColumn}`}
      >
        <li>
          <label htmlFor="title" className={styles.form__label}>
            {t('NAME_2')}</label>
          <input
            type="text"
            className={`${styles.form__input}`}
            name="title"
            id="title"
            value={form.title}
            onChange={handleChange}
          />
        </li>
        <li>
          <label htmlFor="body" className={styles.form__label}>
            {t('MESSAGE_NOTIFICATION')}</label>
          <textarea
            className={`${styles.form__input} ${styles.minHeight_type_s}`}
            ref={textAreaRef}
            rows={1}
            name="body"
            id="body"
            value={form.body}
            onChange={handleChange}
          />
        </li>
        <li>
          <label htmlFor="statusId" className={styles.form__label}>{t('COLUMN_STATUS')}</label>
          <Select
            name={"statusId"}
            options={statusOptions}
            value={statusOptions.find(
              (opt) => opt.value === form.statusId?.toString()
            )}
            placeholder={t('STATUS_PLACEHOLDER')}
            className={styles.select}
            onChange={handleSelectChange}
          />
        </li>
      </ul>
      <button
        type={"button"}
        className={`button ${styles.form__btn} ${
          (!form.title || !form.body) && styles.form__btn_disabled
        } `}
        disabled={!form.title || !form.body}
        onClick={onSubmit}
      >
        {isLoading ? t('SUBMITTING_LABEL') : form.id ? t('TO_EDIT') : t('CREATE')}
      </button>
      {openModalSuccess && (
        <Modal
          onClose={() => {
            setOpenModalSuccess(false);
            onClose();
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
              onClose();
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </form>
  );
}

export default AddOrEditSmsForm;
